import type {
    OrganizationPartnership,
    NetworkPracticeSearchResult,
    NetworkFacilitySearchResult,
    PartnershipStatus,
    NetworkDashboardStats,
    PartnerInteropStats,
    NetworkActivityItem,
    ActivityItemType,
} from "@/types/organization-partnership";

export function mapPartnershipRow(row: Record<string, unknown>): OrganizationPartnership {
    return {
        id: row.id as string,
        requesterType: row.requester_type as OrganizationPartnership["requesterType"],
        requesterPracticeId: (row.requester_practice_id as string) ?? null,
        requesterFacilityId: (row.requester_facility_id as string) ?? null,
        targetType: row.target_type as OrganizationPartnership["targetType"],
        targetPracticeId: (row.target_practice_id as string) ?? null,
        targetFacilityId: (row.target_facility_id as string) ?? null,
        status: row.status as PartnershipStatus,
        requestedCategories: (row.requested_categories as string[]) ?? [],
        approvedCategories: (row.approved_categories as string[]) ?? [],
        message: (row.message as string) ?? null,
        rejectionReason: (row.rejection_reason as string) ?? null,
        requestedBy: (row.requested_by as string) ?? null,
        respondedBy: (row.responded_by as string) ?? null,
        requestedAt: row.requested_at as string,
        respondedAt: (row.responded_at as string) ?? null,
        createdAt: row.created_at as string,
        updatedAt: row.updated_at as string,
        partnerName: row.partner_name as string | undefined,
        partnerCity: row.partner_city as string | undefined,
        partnerPostalCode: row.partner_postal_code as string | undefined,
        partnerSpecialty: row.partner_specialty as string | undefined,
        partnerFacilityType: row.partner_facility_type as string | undefined,
        partnerIsVerified: row.partner_is_verified as boolean | undefined,
    };
}

export function mapPartnerRpcRow(row: Record<string, unknown>): OrganizationPartnership {
    const partnerType = (row.partner_type as string) ?? "practice";
    return {
        id: (row.partnership_id as string) ?? "",
        requesterType: "care",
        requesterPracticeId: null,
        requesterFacilityId: null,
        targetType: partnerType as OrganizationPartnership["targetType"],
        targetPracticeId: partnerType === "practice" ? (row.partner_id as string) ?? null : null,
        targetFacilityId: partnerType === "care" ? (row.partner_id as string) ?? null : null,
        status: (row.partnership_status as PartnershipStatus) ?? "AKTIV",
        requestedCategories: [],
        approvedCategories: (row.approved_categories as string[]) ?? [],
        message: null,
        rejectionReason: null,
        requestedBy: null,
        respondedBy: null,
        requestedAt: (row.created_at as string) ?? "",
        respondedAt: null,
        createdAt: (row.created_at as string) ?? "",
        updatedAt: (row.created_at as string) ?? "",
        partnerName: row.partner_name as string | undefined,
        partnerCity: row.partner_city as string | undefined,
        partnerPostalCode: row.partner_postal_code as string | undefined,
        partnerSpecialty: row.partner_specialty as string | undefined,
        partnerIsVerified: row.partner_is_verified as boolean | undefined,
    };
}

export function mapPendingRequestRpcRow(row: Record<string, unknown>): OrganizationPartnership {
    const reqType = (row.requester_type as string) ?? "practice";
    return {
        id: (row.partnership_id as string) ?? "",
        requesterType: reqType as OrganizationPartnership["requesterType"],
        requesterPracticeId: reqType === "practice" ? (row.requester_id as string) ?? null : null,
        requesterFacilityId: reqType === "care" ? (row.requester_id as string) ?? null : null,
        targetType: reqType === "practice" ? "care" : "practice",
        targetPracticeId: null,
        targetFacilityId: null,
        status: "ANGEFRAGT" as PartnershipStatus,
        requestedCategories: (row.requested_categories as string[]) ?? [],
        approvedCategories: [],
        message: (row.message as string) ?? null,
        rejectionReason: null,
        requestedBy: null,
        respondedBy: null,
        requestedAt: (row.requested_at as string) ?? "",
        respondedAt: null,
        createdAt: (row.requested_at as string) ?? "",
        updatedAt: (row.requested_at as string) ?? "",
        partnerName: row.requester_name as string | undefined,
    };
}

export function mapPracticeSearchRow(row: Record<string, unknown>): NetworkPracticeSearchResult {
    return {
        type: "practice",
        id: row.id as string,
        name: row.name as string,
        city: (row.city as string) ?? null,
        postalCode: (row.postal_code as string) ?? null,
        specialty: (row.specialty as string) ?? null,
        phone: (row.phone as string) ?? null,
        email: (row.email as string) ?? null,
        isVerified: (row.is_verified as boolean) ?? false,
        partnershipStatus: (row.partnership_status as PartnershipStatus) ?? null,
        partnershipRequestedAt: row.partnership_requested_at ? String(row.partnership_requested_at) : null,
    };
}

export function mapFacilitySearchRow(row: Record<string, unknown>): NetworkFacilitySearchResult {
    return {
        type: "care",
        id: row.id as string,
        name: row.name as string,
        city: (row.city as string) ?? null,
        postalCode: (row.postal_code as string) ?? null,
        facilityType: (row.facility_type as string) ?? null,
        totalBeds: (row.total_beds as number) ?? null,
        specializations: (row.specializations as string[]) ?? null,
        phone: (row.phone as string) ?? null,
        email: (row.email as string) ?? null,
        isVerified: (row.is_verified as boolean) ?? false,
        partnershipStatus: (row.partnership_status as PartnershipStatus) ?? null,
        partnershipRequestedAt: row.partnership_requested_at ? String(row.partnership_requested_at) : null,
    };
}

export function mapFacilitiesMetaRow(row: Record<string, unknown>): { totalFacilities: number; facilityTypes: string[] } {
    return {
        totalFacilities: (row.total_facilities as number) ?? 0,
        facilityTypes: (row.facility_types as string[]) ?? [],
    };
}

export function mapRegistryMetaRow(row: Record<string, unknown>): { totalPractices: number; specialties: string[] } {
    return {
        totalPractices: (row.total_practices as number) ?? 0,
        specialties: (row.specialties as string[]) ?? [],
    };
}

export function mapDashboardStatsRow(row: Record<string, unknown>): NetworkDashboardStats {
    return {
        activePartners: (row.active_partners as number) ?? 0,
        pendingRequests: (row.pending_requests as number) ?? 0,
        activeCareLinks: (row.active_care_links as number) ?? 0,
        openCareRequests: (row.open_care_requests as number) ?? 0,
        activeReferrals: (row.active_referrals as number) ?? 0,
    };
}

export function mapPartnerInteropRow(row: Record<string, unknown>): PartnerInteropStats {
    return {
        partnerId: (row.partner_id as string) ?? "",
        partnerType: (row.partner_type as PartnerInteropStats["partnerType"]) ?? "practice",
        partnerName: (row.partner_name as string) ?? "",
        partnershipStatus: (row.partnership_status as PartnershipStatus) ?? "AKTIV",
        careLinkCount: (row.care_link_count as number) ?? 0,
        openRequestCount: (row.open_request_count as number) ?? 0,
        activeReferralCount: (row.active_referral_count as number) ?? 0,
        lastActivity: (row.last_activity as string) ?? null,
        approvedCategories: (row.approved_categories as string[]) ?? [],
    };
}

export function mapActivityFeedRow(row: Record<string, unknown>): NetworkActivityItem {
    return {
        activityId: (row.activity_id as string) ?? "",
        activityType: (row.activity_type as ActivityItemType) ?? "partnership",
        title: (row.title as string) ?? "",
        subtitle: (row.subtitle as string) ?? "",
        partnerName: (row.partner_name as string) ?? "",
        partnerId: (row.partner_id as string) ?? "",
        status: (row.status as string) ?? "",
        createdAt: (row.created_at as string) ?? "",
    };
}